/**
 * Three ways to get a report out of the tab that made it.
 *
 * Every report this layer produces is, until this module, a React tree. Close
 * the tab and the scan is gone; there is nothing to paste into the thread where
 * the pack is being discussed, nothing to attach to a pull request, and nothing
 * to compare a second scan against. That is not a hypothetical cost. It is the
 * single thing the competitor's twenty-four free tools all have in common: not
 * one of them can export anything, and their own documentation tells users to
 * rescan and compare like-for-like — advice their tools make impossible, because
 * there is no artifact of the first scan to compare to.
 *
 * A finding is only worth producing if it can reach the person who can act on
 * it, and that person is usually not the person running the scan: it is whoever
 * built the pack. So the export is the product, not a convenience on top of it.
 * Markdown for a Discord thread or a pull request, CSV for a spreadsheet, JSON
 * for anyone scripting against a batch of packs.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE DEFECT THIS MODULE IS WRITTEN AGAINST
 *
 * `GtaResourceReport.findings` is not the report. It is the resource-level half
 * of it; the other half — usually the larger half, and always the half a modder
 * cares about — hangs off `vehicles[].findings`, because `analyzeResource` keeps
 * a vehicle's problems attached to the vehicle so that one misspelled
 * `handlingId` reads as one car with eight consequences rather than eight loose
 * errors. An exporter that reads the top level and stops produces a file that is
 * shorter than the screen, silently, and looks complete. That is the green-tick
 * failure `analyze.ts` was written against, reproduced in a new format, and it
 * is the reason this module flattens both levels and keeps the attribution.
 *
 * The same applies one level up: a `GtaResourceSetReport` holds a report per
 * resource *plus* the findings that only exist between them — the stream
 * basename collision no single resource can see. Both reach the output.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IS DELIBERATELY NOT DECIDED HERE
 *
 * Order. `sortFindings` and `dedupeFindings` in `findings.ts` are the report
 * order every surface renders, and an export that disagrees with the screen it
 * was copied from is a bug report waiting to happen — the reader compares the
 * paste to the panel, finds them different, and stops trusting both. So the
 * ordering is imported, never re-invented, and it is applied over the flattened
 * set so that a vehicle finding can sort above a resource finding when it is
 * worse. Severity, then the emergency fleet, then entity, file, line.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PROVENANCE
 *
 * A report pasted into Discord is read months later, without the tab, without
 * the files, and without any way to re-run it. So every Markdown export ends
 * with the sentence that makes it readable then: what made it, when, and which
 * stock-asset index the vanilla checks were run against — the game build and the
 * date that index was generated. A collision check against a build from before
 * the DLC that added the name is a different statement from one against the
 * current build, and six months on, nobody can tell which they are holding.
 *
 * That stamp is a statement about *the report in hand*, and it is derived from
 * the report before anything else is consulted. The obvious implementation —
 * ask the module cache what index is loaded — describes the session instead, and
 * a session and a report disagree in both directions. A surface that loaded the
 * index on mount and then scanned a folder without it produces a report whose
 * own skipped list says the vanilla rules did not run, under a footer citing a
 * game build: the reader of the paste has a build number and no reason to
 * distrust it. And an index a caller built from its own dump never reaches that
 * cache at all, so the mirror failure stamps "no name here was checked against
 * the base game" onto a report that is listing base-game overrides. So
 * `report.skipped` decides whether the check ran, and only then is an index
 * consulted for the build it ran against.
 *
 * The index is read with `peekVanillaAssetNameIndex`, never with the loader. An
 * export is a copy button; a copy button must not start an 857 KB download. When
 * the report says the check ran but no index can be named for it, the stamp says
 * that rather than inventing either half.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LIMITS
 *
 *  - `findings.ts` caps every operation at 20,000 findings, per call and
 *    privately, so a single resource past that loses its tail here as it does
 *    everywhere else and this module cannot tell a capped list from a
 *    deduplicated one. What it does not do is let the cap reach the headline: a
 *    set of resources is summarised over every row the file actually prints, so
 *    an export can never announce "Export possible" above the errors it lists.
 *    See `countSeverities`.
 *  - The generation date is printed when the index carries one and as an em dash
 *    when it does not. Both branches are real: the shipped index carries
 *    `generated` from `vanilla-names-v1.json`, and an index a caller built from
 *    its own dump usually carries no date at all. An em dash here therefore means
 *    "this index did not say", never "this build cannot read it".
 *  - The stamp takes the report's skipped list at its word. Every analyzer in
 *    this layer records what it could not do, so silence there is read as "the
 *    rules ran" — and a producer that records no skips at all would be believed.
 *    A report carrying no skip list whatsoever is the one case that cannot be
 *    read either way, and it is stamped as unknown rather than as checked.
 *  - A clean export is a clean *static* check. Nothing here has run the
 *    resource, and the note under the summary says so once, plainly.
 *  - None of the three functions throws. They are called from a copy button,
 *    where an exception costs the user the report they were trying to save, so
 *    every field is read defensively and a missing one exports blank.
 */

import type { GtaResourceSetReport } from "./analyze";
import { csvCell } from "./csv";
import { disambiguateLabels, EM_DASH_LABEL_STYLE } from "./labels";
import {
  dedupeFindings,
  describeVerdict,
  isEmergencyFinding,
  sortFindings,
  summarizeFindings,
  verdictFor,
} from "./findings";
import type {
  GtaCheck,
  GtaEntity,
  GtaFinding,
  GtaFindingTag,
  GtaFix,
  GtaResourceReport,
  GtaSeverity,
  GtaTextEdit,
  VanillaAssetNameIndex,
} from "./types";
import { peekVanillaAssetNameIndex } from "./vanillaAssetNames";

// ─── Identity ────────────────────────────────────────────────────────────────

/** Who made a report, so one found months later can be placed. */
export const GTA_REPORT_TOOL = "The Workshop — GTA resource checker";

/** The JSON document's shape, so a script can refuse a version it cannot read. */
export const GTA_REPORT_SCHEMA = "gta-report/v1";

/**
 * The one caveat every tool in this plan is supposed to say once, plainly.
 *
 * The claim a report can support is that these files are consistent with each
 * other. It cannot support the claim that the resource works, because nothing
 * here has started a server. Saying so in the exported artifact matters more
 * than saying it on screen: the screen has the scan next to it, the paste does
 * not.
 */
export const GTA_REPORT_STATIC_CHECK_NOTE =
  "This is a static check of the files as they were read. It says these files agree with each " +
  "other; it is not proof that the resource behaves in game.";

/** What the layer prints where it has no value and refuses to invent one. */
const EM_DASH = "—";

// ─── Options ─────────────────────────────────────────────────────────────────

/**
 * Everything the three exporters take beyond the report itself.
 *
 * The required signature is the one-argument form — `toMarkdown(report)` is what
 * a copy button calls and every field here has a defensible default. `generatedAt`
 * is a parameter rather than a clock reading for the reason `resmon.ts` gives:
 * it makes the stamp a value, so a test can assert on the sentence and two
 * exports of one report differ only where the report did.
 */
export type GtaReportExportOptions = {
  /** Overrides the generated heading, for a tool that has a better name for it. */
  title?: string;
  /** The moment to stamp. Defaults to now. */
  generatedAt?: Date;
  /**
   * The stock-name index the analysis actually used.
   *
   * A caller that built its own index — a different game build, a server's own
   * inventory — should pass it, because the report records that its vanilla
   * rules ran but not which index they ran against. Omitted, and only where the
   * report says those rules did run, the stamp falls back to the index already
   * loaded in this tab; where it cannot name one it says so rather than
   * guessing in either direction.
   */
  vanillaNames?: VanillaAssetNameIndex | null;
};

// ─── Rows ────────────────────────────────────────────────────────────────────

/**
 * Where a finding sat in the report it came out of.
 *
 * Flattening without this would answer the wrong question: "eight errors" is not
 * a report, "this vehicle has eight errors and they are all downstream of one
 * name" is. The scope survives into every format so the attribution is not lost
 * in the one artifact that outlives the screen.
 */
export type GtaExportScope = "cross-resource" | "resource" | "vehicle";

export type GtaExportRow = {
  scope: GtaExportScope;
  /** The resource this came from; empty for a finding that is about the set. */
  resource: string;
  /**
   * The archive or pick that resource came out of; empty when it is not known.
   *
   * Optional and beside `resource` rather than folded into it, because the two
   * answer different questions and a spreadsheet has to be able to sort on
   * either. `resource` stays the FiveM name a user recognises even when two rows
   * share it — this is the column that then says which of the two a row is.
   */
  origin?: string;
  /**
   * The unique root that resource was found at; empty when it is not known.
   *
   * Added because `origin` cannot always do the job asked of it. An origin is
   * per-container: one `server.zip` holding `[vehicles]/police` and
   * `[els]/police` gives both the same one, and a picked `resources/` folder
   * gives every pack in it the same one. Two rows then agree on `resource` and
   * on `origin` and there is no third field to tell them apart — the collapse
   * the identity thread exists to prevent, surviving into the spreadsheet.
   *
   * Optional, and last of the three on purpose: it is a key rather than
   * something a user recognises, so it is the tiebreaker and never the label.
   */
  identity?: string;
  /** The vehicle it was attached to, as written; empty when it was not. */
  vehicle: string;
  finding: GtaFinding;
};

// ─── Reading a report defensively ────────────────────────────────────────────
//
// Everything below reads `unknown` rather than the declared types. That is not
// distrust of `analyze.ts`: these functions are wired to a copy button, and the
// report reaching them may have come from `localStorage`, from a desktop build
// older than this module, or from a half-finished scan a user copied mid-run. An
// exception in a copy handler loses the user the very thing they were trying to
// save, so a field that is not what it claims exports blank instead.

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asList(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asText(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function asNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

/**
 * One finding, copied into a shape the rest of this module can render.
 *
 * The copy is the point. `dedupeFindings` compares `finding.file` through
 * `pathKey`, which is string work — hand it a number and the whole export throws
 * from inside a module that is not at fault. Normalising first means the
 * shared ordering can be reused as-is on input it was never written for.
 *
 * A severity this layer does not define is kept rather than corrected: it is
 * what the analyzer said, `findings.ts` already ranks an unknown severity last
 * instead of poisoning the sort, and rewriting it to "info" here would be this
 * module inventing a judgement it has no basis for.
 */
function normalizeFinding(raw: unknown): GtaFinding | null {
  const record = asRecord(raw);
  if (!record) return null;

  const finding: GtaFinding = {
    code: asText(record.code) as GtaFinding["code"],
    severity: asText(record.severity) as GtaSeverity,
    title: asText(record.title),
    detail: asText(record.detail),
  };

  const file = asText(record.file);
  if (file !== "") finding.file = file;
  const line = asNumber(record.line);
  if (line !== undefined) finding.line = line;
  const column = asNumber(record.column);
  if (column !== undefined) finding.column = column;
  const byteStart = asNumber(record.byteStart);
  if (byteStart !== undefined) finding.byteStart = byteStart;
  const byteEnd = asNumber(record.byteEnd);
  if (byteEnd !== undefined) finding.byteEnd = byteEnd;

  const entity = asRecord(record.entity);
  if (entity) {
    finding.entity = {
      kind: asText(entity.kind) as GtaEntity["kind"],
      id: asText(entity.id),
    };
  }

  const tags = asList(record.tags).filter((tag): tag is string => typeof tag === "string");
  if (tags.length > 0) finding.tags = tags as GtaFindingTag[];

  const fix = asRecord(record.fix);
  if (fix) finding.fix = normalizeFix(fix);

  return finding;
}

function normalizeFix(fix: Record<string, unknown>): GtaFix {
  const confidence = asText(fix.confidence);
  const normalized: GtaFix = {
    label: asText(fix.label),
    confidence:
      confidence === "high" || confidence === "medium" || confidence === "low"
        ? confidence
        : ("low" as const),
  };

  const edits: GtaTextEdit[] = [];
  for (const raw of asList(fix.edits)) {
    const edit = asRecord(raw);
    if (!edit) continue;
    edits.push({
      file: asText(edit.file),
      start: asNumber(edit.start) ?? 0,
      end: asNumber(edit.end) ?? 0,
      // `expect` is a `RegExp | string`, and a RegExp survives `JSON.stringify`
      // as `{}` — an exported edit that claims to have no expectation to verify,
      // which is the opposite of what the field is for. Its source text is kept
      // instead, so the JSON says what the edit checks even though only the
      // in-memory form can still check it.
      expect: typeof edit.expect === "string" ? edit.expect : String(edit.expect ?? ""),
      replacement: asText(edit.replacement),
    });
  }
  if (edits.length > 0) normalized.edits = edits;

  return normalized;
}

// ─── Flattening ──────────────────────────────────────────────────────────────

type FlatVehicle = {
  model: string;
  emergency: boolean;
  handlingId: string;
  findingCount: number;
};

type FlatResource = {
  name: string;
  /**
   * What tells this resource from the one next to it with the same name, and the
   * archive it came from. Both may be empty — a single-folder scan has neither
   * to give — and `resourceLabels` below decides what to print from them, as a
   * set rather than one at a time, because ambiguity is a property of the set.
   */
  identity: string;
  origin: string;
  kind: string;
  verdict: string;
  checks: GtaCheck[];
  skipped: string[];
  vehicles: FlatVehicle[];
  streamAssets: number;
  manifest: { file: string; legacy: boolean; fxVersion: string | null; games: string[] } | null;
  rows: GtaExportRow[];
};

type FlatReport = {
  kind: "resource" | "resource-set";
  resources: FlatResource[];
  /** Findings that belong to the set rather than to any one resource. */
  cross: GtaExportRow[];
  /** Skips recorded by the set that no single resource recorded. */
  crossSkipped: string[];
  /** Every skip, deduplicated, in the order the report recorded them. */
  skipped: string[];
  /**
   * Entries that were in the report and could not be read as findings.
   *
   * Counted rather than dropped in silence. A report that quietly loses rows on
   * the way out is the same failure as one that quietly skips rules.
   */
  unreadable: number;
};

/**
 * Whether this is a set of resources rather than one.
 *
 * `resources` is the discriminator because it is the field that only the set
 * report has, and it is checked as an array rather than for existence so a
 * malformed object carrying an unrelated `resources` string does not send the
 * exporter down the wrong branch.
 */
export function isResourceSetReport(value: unknown): value is GtaResourceSetReport {
  const record = asRecord(value);
  return record !== null && Array.isArray(record.resources);
}

/** The rows of one resource: its own findings and every vehicle's, as one list. */
function flattenResource(raw: unknown): { resource: FlatResource; unreadable: number } {
  const record = asRecord(raw) ?? {};
  const name = asText(record.name);

  // Attribution is carried on the side, keyed by object identity, because
  // `dedupeFindings` and `sortFindings` return the same objects they were given.
  // Threading a scope field through the finding itself would mean handing the
  // shared ordering a shape it does not define.
  const origin = new Map<GtaFinding, { scope: GtaExportScope; vehicle: string }>();
  const collected: GtaFinding[] = [];
  let unreadable = 0;

  for (const entry of asList(record.findings)) {
    const finding = normalizeFinding(entry);
    if (!finding) {
      unreadable += 1;
      continue;
    }
    origin.set(finding, { scope: "resource", vehicle: "" });
    collected.push(finding);
  }

  const vehicles: FlatVehicle[] = [];
  for (const entry of asList(record.vehicles)) {
    const vehicle = asRecord(entry);
    if (!vehicle) {
      unreadable += 1;
      continue;
    }
    const model = asText(vehicle.modelRaw) || asText(vehicle.model);
    const own = asList(vehicle.findings);
    vehicles.push({
      model,
      emergency: vehicle.emergency === true,
      handlingId: asText(vehicle.handlingId),
      findingCount: own.length,
    });
    for (const entry2 of own) {
      const finding = normalizeFinding(entry2);
      if (!finding) {
        unreadable += 1;
        continue;
      }
      origin.set(finding, { scope: "vehicle", vehicle: model });
      collected.push(finding);
    }
  }

  const from = asText(record.origin);
  const root = asText(record.identity);
  const ordered = sortFindings(dedupeFindings(collected));
  const rows: GtaExportRow[] = ordered.map((finding) => {
    const where = origin.get(finding) ?? { scope: "resource" as const, vehicle: "" };
    return {
      scope: where.scope,
      resource: name,
      origin: from,
      identity: root,
      vehicle: where.vehicle,
      finding,
    };
  });

  const manifest = asRecord(record.manifest);

  return {
    resource: {
      name,
      identity: asText(record.identity),
      origin: asText(record.origin),
      kind: asText(record.kind),
      verdict: asText(record.verdict),
      checks: asList(record.checks)
        .map((entry) => asRecord(entry))
        .filter((entry): entry is Record<string, unknown> => entry !== null)
        .map((entry) => ({
          label: asText(entry.label),
          passed: asNumber(entry.passed) ?? 0,
          total: asNumber(entry.total) ?? 0,
        })),
      skipped: asList(record.skipped).map(asText).filter((text) => text !== ""),
      vehicles,
      streamAssets: asList(record.stream).length,
      manifest: manifest
        ? {
            file: asText(manifest.file),
            legacy: manifest.legacy === true,
            fxVersion: typeof manifest.fxVersion === "string" ? manifest.fxVersion : null,
            games: asList(manifest.games).map(asText).filter((game) => game !== ""),
          }
        : null,
      rows,
    },
    unreadable,
  };
}

/** Both report shapes, read into the one shape the three formats render. */
function flatten(report: unknown): FlatReport {
  if (isResourceSetReport(report)) {
    const record = asRecord(report) ?? {};
    const resources: FlatResource[] = [];
    let unreadable = 0;
    for (const entry of asList(record.resources)) {
      const flat = flattenResource(entry);
      resources.push(flat.resource);
      unreadable += flat.unreadable;
    }

    const crossFindings: GtaFinding[] = [];
    for (const entry of asList(record.findings)) {
      const finding = normalizeFinding(entry);
      if (!finding) {
        unreadable += 1;
        continue;
      }
      crossFindings.push(finding);
    }
    const cross: GtaExportRow[] = sortFindings(dedupeFindings(crossFindings)).map((finding) => ({
      scope: "cross-resource" as const,
      resource: "",
      vehicle: "",
      finding,
    }));

    const setSkipped = asList(record.skipped).map(asText).filter((text) => text !== "");
    const fromResources = new Set(resources.flatMap((resource) => resource.skipped));
    return {
      kind: "resource-set",
      resources,
      cross,
      // `analyzeResourceSet` already unions its resources' skips into its own
      // list, so the set-level extras are the ones no resource could have known
      // about — the vanilla index that was never loaded, for instance.
      crossSkipped: setSkipped.filter((text) => !fromResources.has(text)),
      skipped: [...new Set([...setSkipped, ...fromResources])],
      unreadable,
    };
  }

  const flat = flattenResource(report);
  return {
    kind: "resource",
    resources: [flat.resource],
    cross: [],
    crossSkipped: [],
    skipped: [...new Set(flat.resource.skipped)],
    unreadable: flat.unreadable,
  };
}

/** Every finding in the export, cross-resource first, in report order. */
function allRows(flat: FlatReport): GtaExportRow[] {
  // Cross-resource findings lead because they are the reason a set was scanned
  // at all: a collision between two packs is invisible from inside either one,
  // and burying it under the first resource's own list is how it gets missed.
  return [...flat.cross, ...flat.resources.flatMap((resource) => resource.rows)];
}

function allFindings(flat: FlatReport): GtaFinding[] {
  return allRows(flat).map((row) => row.finding);
}

// ─── Counting what the file actually prints ──────────────────────────────────

type SeverityCounts = { error: number; warning: number; info: number };

/**
 * A slice small enough that the cap inside `findings.ts` cannot reach it.
 *
 * The cap is 20,000 and it is private to that module, so this is deliberately an
 * order of magnitude under it rather than one less than it: a number chosen to
 * survive the cap being lowered, not to sit exactly beneath today's value.
 */
const COUNT_SLICE = 2000;

/**
 * How many of each severity, over every row rather than the first 20,000.
 *
 * `summarizeFindings` stays the counter — what counts as an error is a decision
 * that belongs to `findings.ts`, and a second tally here is how two surfaces
 * start disagreeing about one folder — but every function in that module applies
 * its own 20,000 cap, and the concatenated list this module summarises is the
 * shape that goes past it. No single resource has to be anywhere near the cap:
 * §0.4 of the brief names the input that gets there, "a scan result over three
 * hundred resources", and three hundred resources need 67 findings each.
 *
 * Counted straight from the concatenation, the headline describes the first
 * 20,000 rows of a file that then prints all of them — "Export possible, but
 * 20000 warnings" above five thousand errors, an emergency-fleet line counted
 * over one set of findings and a severity line counted over another, and a JSON
 * summary whose severities do not add up to its own `findings` total. A document
 * that contradicts itself is worse than one that is merely incomplete: the
 * reader cannot tell which half to believe, and the half they are most likely to
 * believe is the headline.
 *
 * So the shared counter is folded over slices it can see all of, and the parts
 * are added. Reimplementing the tally here would be the second copy this
 * directory does not allow.
 */
function countSeverities(findings: readonly GtaFinding[]): SeverityCounts {
  const total: SeverityCounts = { error: 0, warning: 0, info: 0 };
  for (let start = 0; start < findings.length; start += COUNT_SLICE) {
    const part = summarizeFindings(findings.slice(start, start + COUNT_SLICE));
    total.error += part.error;
    total.warning += part.warning;
    total.info += part.info;
  }
  return total;
}

/**
 * The verdict sentence, over those counts rather than over a capped list.
 *
 * `verdictFor` is imported rather than reimplemented: "one error blocks, however
 * many warnings sit beside it" is findings.ts's judgement, and this module has
 * no standing to hold a second opinion about it. It is asked over a witness list
 * carrying one finding per severity present, which is under any cap by
 * construction and returns exactly the verdict the full list would, because the
 * rule reads which severities are present and not how many of each there are.
 *
 * Only the sentence is assembled here, and only in the warnings case, where
 * `describeVerdict` takes its number from `summarizeFindings` and would print
 * the capped one — the headline has to count the rows underneath it. It is
 * character for character that function's sentence, singular included, and
 * `report.test.ts` asserts the two agree on every report under the cap so the
 * two cannot drift apart in silence.
 */
function headlineVerdict(counts: SeverityCounts): string {
  const witness: GtaFinding[] = [];
  const witnessOf = (severity: GtaSeverity): GtaFinding => ({
    code: "" as GtaFinding["code"],
    severity,
    title: "",
    detail: "",
  });
  if (counts.error > 0) witness.push(witnessOf("error"));
  if (counts.warning > 0) witness.push(witnessOf("warning"));
  if (counts.info > 0) witness.push(witnessOf("info"));

  if (verdictFor(witness) !== "warnings") return describeVerdict(witness);
  return `Export possible, but ${counts.warning} ${counts.warning === 1 ? "warning" : "warnings"}`;
}

// ─── Shared text helpers ─────────────────────────────────────────────────────
//
// `csvCell` is no longer one of these. It was the copy that mattered — the two
// versions were not equivalent, and the weaker one guarded the less trustworthy
// input — so it now lives in `csv.ts`, which both this module and `resmon.ts`
// import. The argument for a third module rather than one importing the other is
// there.
//
// `mdCell` and the date stamps below are still second copies of `resmon.ts`'s,
// deliberately: they are module-private there, they are three lines each, and
// the two implementations genuinely agree. They are candidates for the same
// treatment if a third exporter ever needs them, and no more than that.

function oneLine(value: string): string {
  return value.replace(/\s*[\r\n]+\s*/g, " ").trim();
}

/** A Markdown table cell: pipes escaped, never more than one line. */
function mdCell(value: string): string {
  return oneLine(value).replace(/\|/g, "\\|");
}

/** The day a report was made, as a calendar date rather than one locale's idea of one. */
function stampDate(at?: Date): string {
  const when = at instanceof Date && Number.isFinite(at.getTime()) ? at : new Date();
  return when.toISOString().slice(0, 10);
}

function stampInstant(at?: Date): string {
  const when = at instanceof Date && Number.isFinite(at.getTime()) ? at : new Date();
  return when.toISOString();
}

/** `1 warning`, `2 warnings` — the pluralisation bug findings.ts calls out, avoided. */
function plural(count: number, noun: string): string {
  return `${count.toLocaleString("en-US")} ${noun}${count === 1 ? "" : "s"}`;
}

// ─── Provenance ──────────────────────────────────────────────────────────────

/**
 * What the report says about its own vanilla checks — never what the tab says.
 *
 * `ran` is the absence of a recorded skip, not a positive claim by the analyzer,
 * and that asymmetry is deliberate: the analyzers record what they could not do,
 * so the honest reading of silence is "nothing stopped it". `unknown` is for a
 * report whose skip list could not be read at all, where saying either of the
 * other two would be an invention.
 */
export type GtaVanillaCheckState = "ran" | "skipped" | "unknown";

export type GtaVanillaStamp = {
  /** What the report itself says about whether the base-game name rules ran. */
  check: GtaVanillaCheckState;
  /** Whether an index could be named for this report at all. */
  loaded: boolean;
  gameBuild: string | null;
  generated: string | null;
  names: number | null;
  source: string | null;
};

/**
 * Whether a skipped-check sentence is one of the vanilla-index ones.
 *
 * Matched on the prose rather than on the constant, because the three sentences
 * that can appear here are module-private to `streamAssets.ts` and
 * `vehicleMeta.ts`, and exporting them is an edit to modules this deliverable
 * does not own. The wording is pinned from the other side instead: the tests
 * build their reports through `analyzeResource` and `analyzeResourceSet`, so a
 * reworded skip that stops matching here fails there rather than quietly
 * restoring the stamp that lies. Both halves are required — "vanilla"/"base
 * game" alone matches the carcols skip, which is about mod kit ids and says
 * nothing about the name index.
 */
function isVanillaIndexSkip(text: string): boolean {
  return /\bindex\b/i.test(text) && /vanilla|base[ -]game/i.test(text);
}

/**
 * The report's own account of whether its names were checked against the game.
 *
 * Read from the resource's `skipped` and, for a set, from every resource's as
 * well as the set's own. `analyzeResourceSet` already unions them, but a report
 * that arrived from a desktop build or from `localStorage` may not have, and the
 * failure of missing one is the stamp claiming a check that never ran.
 */
function vanillaCheckState(report: unknown): GtaVanillaCheckState {
  const record = asRecord(report);
  if (!record) return "unknown";

  const lists: unknown[] = [record.skipped];
  for (const entry of asList(record.resources)) {
    const resource = asRecord(entry);
    if (resource) lists.push(resource.skipped);
  }

  let readable = false;
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    readable = true;
    for (const text of list) {
      if (typeof text === "string" && isVanillaIndexSkip(text)) return "skipped";
    }
  }
  return readable ? "ran" : "unknown";
}

/**
 * What this report's vanilla checks were run against, or the fact that they
 * were not.
 *
 * The report decides first and the index cannot overrule it. A caller may hand
 * in an index the report never used, and the module cache answers for the tab
 * rather than for the scan; both produce a footer that contradicts the findings
 * printed above it, in opposite directions. Only once the report says the rules
 * ran is an index consulted, and then only for the build, date and size it ran
 * against.
 *
 * `peekVanillaAssetNameIndex` rather than the loader, always: an export must not
 * start a multi-megabyte fetch, and a copy button that hangs is a copy button
 * nobody presses twice.
 *
 * `generated` is read off the index rather than hard-coded, which is the
 * difference between a stamp that follows the data file and a second copy of a
 * date that drifts from it. It is read defensively, like every other field here:
 * the type declares it optional because an index built from a caller's own dump
 * has no date to declare, and a report says so with an em dash rather than
 * borrowing the shipped index's date for a list it never read.
 */
export function vanillaIndexStamp(
  report: GtaResourceReport | GtaResourceSetReport | null,
  index?: VanillaAssetNameIndex | null,
): GtaVanillaStamp {
  const check = report === null ? "unknown" : vanillaCheckState(report);
  const blank = { gameBuild: null, generated: null, names: null, source: null };
  if (check !== "ran") return { check, loaded: false, ...blank };

  const resolved = index === undefined ? peekVanillaAssetNameIndex() : index;
  if (!resolved) return { check, loaded: false, ...blank };

  return {
    check,
    loaded: true,
    gameBuild: typeof resolved.gameBuild === "string" ? resolved.gameBuild : null,
    generated: typeof resolved.generated === "string" ? resolved.generated : null,
    names: asNumber(resolved.size) ?? null,
    source: typeof resolved.source === "string" ? resolved.source : null,
  };
}

/** The stamp as the sentence that goes in the artifact. */
export function describeVanillaIndex(stamp: GtaVanillaStamp): string {
  if (stamp.check === "skipped") {
    return (
      "Stock asset names: not checked. This report records that the base-game name index was " +
      "missing or unreadable, so no name in it was compared against the base game — the reason " +
      "is with the checks that did not run."
    );
  }
  if (stamp.check === "unknown") {
    return (
      "Stock asset names: this report does not say whether its names were checked against the " +
      "base game, so treat them as unchecked."
    );
  }
  if (!stamp.loaded) {
    // The check ran, so saying it did not would be the same lie in reverse. What
    // is missing is only the identity of the index, and the caller is the one who
    // can supply it.
    return (
      "Stock asset names: checked against a base-game index this report does not identify. Pass " +
      "the index to the exporter to stamp its game build here."
    );
  }
  return (
    `Stock asset names: game build ${stamp.gameBuild ?? EM_DASH}, index generated ` +
    `${stamp.generated ?? EM_DASH}, ${
      stamp.names === null ? EM_DASH : stamp.names.toLocaleString("en-US")
    } names.`
  );
}

/**
 * The one sentence an export has to carry: what made it, when, against what.
 *
 * Same shape as `resmonSourceStamp`, on purpose. Two exports from the same hub
 * that footnote themselves differently read as two tools by two authors, and the
 * reader has to learn both.
 *
 * The report is a parameter and not optional: the middle clause is a claim about
 * that report, and a stamp that could be written without one would be written
 * without one. `null` is the failure path below, where the report defeated the
 * exporter and the honest answer is that nothing is known about it.
 */
export function gtaReportSourceStamp(
  report: GtaResourceReport | GtaResourceSetReport | null,
  options: GtaReportExportOptions = {},
): string {
  const vanilla = describeVanillaIndex(vanillaIndexStamp(report, options.vanillaNames));
  return (
    `Generated by ${GTA_REPORT_TOOL} on ${stampDate(options.generatedAt)}. ${vanilla} ` +
    "Analysed on this machine; nothing in this report was uploaded anywhere."
  );
}

// ─── Markdown ────────────────────────────────────────────────────────────────

/**
 * How each resource is spelled in a heading and in the summary table.
 *
 * A set report can legitimately hold two resources with the same name — two
 * vendor archives that both ship `police/` — and that duplicate is the finding,
 * not a mistake to be cleaned up. Which means the export is the last place the
 * two can be told apart, and a document with two `## police` headings, each
 * followed by a different list of errors, undoes every bit of work the analyzer
 * did to keep them separate: a reader cannot tell which pack to open.
 *
 * This takes the whole list rather than one resource, because the earlier
 * per-resource version could not see the thing it had to decide. It appended the
 * origin whenever there was one, and an origin is per-container rather than
 * per-resource: a picked `police/` folder has origin `police` and printed
 * `## police — police`; a picked `resources/` folder gives every pack inside it
 * the origin `resources`, so every heading in the document ended in the same
 * word; and one `server.zip` holding `[vehicles]/police` and `[els]/police` —
 * the ordinary shape of a FiveM server — printed `## police — server.zip` twice
 * and two byte-identical table rows, which is the exact collapse the paragraph
 * above says must not happen.
 *
 * The rule now lives in `labels.ts` and is shared with the collision sentence in
 * `streamAssets.ts`, so the finding that names two packs and the sections a
 * reader scrolls to afterwards cannot disagree about how those packs are spelled.
 * A single-folder scan is unaffected: nothing repeats, so nothing is qualified.
 */
function resourceLabels(resources: readonly FlatResource[]): string[] {
  return disambiguateLabels(resources, EM_DASH_LABEL_STYLE);
}

function defaultTitle(flat: FlatReport): string {
  if (flat.kind === "resource-set") {
    return `GTA resource report — ${plural(flat.resources.length, "resource")}`;
  }
  const name = flat.resources[0]?.name ?? "";
  return name === "" ? "GTA resource report" : `GTA resource report — ${name}`;
}

/**
 * `vehicles.meta` line 12, or an em dash where the finding named no place.
 *
 * The em dash rather than a blank or a guessed file, for the reason `resmon.ts`
 * prints one for a column its dump never carried: a location this layer does not
 * have is not the same as the resource root, and inventing one sends the reader
 * to open a file that has nothing to do with the finding.
 *
 * `underResource` is set when the section heading already names the resource, so
 * the qualifier is not repeated on every line under it. The vehicle is never
 * dropped: no heading carries it, and it is the attribution the flattening
 * exists to preserve.
 */
function describeWhere(row: GtaExportRow, underResource: boolean): string {
  const parts: string[] = [];
  const file = row.finding.file;
  if (file) {
    parts.push(
      row.finding.line === undefined ? `\`${file}\`` : `\`${file}\` line ${row.finding.line}`,
    );
  }
  if (row.vehicle !== "") parts.push(`vehicle \`${row.vehicle}\``);
  if (row.scope === "cross-resource") parts.push("across resources");
  else if (!underResource && row.resource !== "") parts.push(`resource \`${row.resource}\``);
  return parts.length === 0 ? EM_DASH : parts.join(" · ");
}

/**
 * One finding as a list item rather than as a table row.
 *
 * The tables in this module are for the counts, matching `resmon.ts`, but a
 * finding is not a row of numbers: two thirds of it is the `detail` sentence
 * that says what happens in game and what to do about it, and a table cell that
 * has to hold three sentences produces a table nobody can read. Discord, which
 * is where most of these get pasted, does not render Markdown tables at all —
 * a list degrades to something legible there and a table degrades to pipes.
 */
function findingLines(row: GtaExportRow, underResource = false): string[] {
  const finding = row.finding;
  // Both fields are read back through `asText` rather than compared against "":
  // they are literal-union types, and this export has to survive a report whose
  // producer filled them with something the union does not list.
  const severity = asText(finding.severity) || EM_DASH;
  const code = asText(finding.code);
  const emergency = isEmergencyFinding(finding) ? " · **emergency fleet**" : "";
  const title = oneLine(finding.title) || "(no title)";

  const lines = [
    `- **${severity}**${code === "" ? "" : ` \`${code}\``} — ${title}${emergency}`,
  ];
  const detail = oneLine(finding.detail);
  if (detail !== "") lines.push(`  ${detail}`);
  lines.push(`  Where: ${describeWhere(row, underResource)}`);
  if (finding.fix && finding.fix.label !== "") {
    lines.push(`  Fix: ${oneLine(finding.fix.label)} (${finding.fix.confidence} confidence)`);
  }
  return lines;
}

function severityLine(counts: SeverityCounts): string {
  return `${plural(counts.error, "error")} · ${plural(counts.warning, "warning")} · ${plural(
    counts.info,
    "info finding",
  )}`;
}

/**
 * The whole report as Markdown, skipped checks and provenance included.
 *
 * The skipped list is not an appendix. It sits with the summary, because a
 * report that dropped half its rules and printed a green tick is the failure
 * this layer exists to prevent, and an export that leaves the caveat behind on
 * the screen has reintroduced it for every reader of the paste.
 */
export function toMarkdown(
  report: GtaResourceReport | GtaResourceSetReport,
  options: GtaReportExportOptions = {},
): string {
  try {
    const flat = flatten(report);
    // Computed once for the whole document: the table row and the heading for one
    // resource have to be the same string, and two calls to a set-wide widener
    // over two different slices would not guarantee that.
    const labels = resourceLabels(flat.resources);
    const rows = allRows(flat);
    const findings = rows.map((row) => row.finding);
    // Counted over `rows`, which is exactly what this document prints below, so
    // the headline and the list can never describe two different sets.
    const counts = countSeverities(findings);
    const out: string[] = [];

    out.push(`# ${options.title ?? defaultTitle(flat)}`, "");
    out.push(`- Verdict: **${headlineVerdict(counts)}**`);
    out.push(`- Findings: **${severityLine(counts)}**`);

    const emergency = findings.filter(isEmergencyFinding).length;
    if (emergency > 0) {
      out.push(`- On emergency-fleet vehicles: **${plural(emergency, "finding")}**`);
    }

    const vehicles = flat.resources.reduce((sum, r) => sum + r.vehicles.length, 0);
    const stream = flat.resources.reduce((sum, r) => sum + r.streamAssets, 0);
    out.push(
      `- Scanned: **${plural(flat.resources.length, "resource")}**, ` +
        `**${plural(vehicles, "vehicle")}**, **${plural(stream, "stream asset")}**`,
    );
    out.push(
      flat.skipped.length === 0
        ? "- Checks that did not run: **none recorded**"
        : `- Checks that did not run: **${flat.skipped.length.toLocaleString("en-US")}** (listed below)`,
    );
    if (flat.unreadable > 0) {
      out.push(
        `- Entries that could not be read and are not listed: **${flat.unreadable.toLocaleString("en-US")}**`,
      );
    }
    out.push("");
    out.push(`> ${GTA_REPORT_STATIC_CHECK_NOTE}`, "");

    if (flat.kind === "resource-set" && flat.resources.length > 0) {
      out.push("## Resources", "");
      out.push("| Resource | Kind | Verdict | Errors | Warnings | Info |");
      out.push("| --- | --- | --- | ---: | ---: | ---: |");
      for (const [index, resource] of flat.resources.entries()) {
        const own = countSeverities(resource.rows.map((row) => row.finding));
        out.push(
          `| ${mdCell(labels[index])} | ${mdCell(resource.kind || EM_DASH)} | ` +
            `${mdCell(resource.verdict || EM_DASH)} | ${own.error} | ${own.warning} | ${own.info} |`,
        );
      }
      out.push("");
    }

    if (flat.kind === "resource-set") {
      out.push("## Between resources", "");
      if (flat.cross.length === 0) {
        out.push(
          "No finding that only exists across the set. Each resource's own findings are below.",
          "",
        );
      } else {
        for (const row of flat.cross) out.push(...findingLines(row));
        out.push("");
      }
    }

    for (const [index, resource] of flat.resources.entries()) {
      out.push(`## ${labels[index]}`, "");

      if (resource.checks.length > 0) {
        out.push("| Check | Passed | Total |");
        out.push("| --- | ---: | ---: |");
        for (const check of resource.checks) {
          out.push(`| ${mdCell(check.label)} | ${check.passed} | ${check.total} |`);
        }
        out.push("");
      }

      if (resource.rows.length === 0) {
        out.push("Nothing was reported for this resource.", "");
        continue;
      }
      for (const row of resource.rows) out.push(...findingLines(row, true));
      out.push("");
    }

    out.push("## Checks that did not run", "");
    if (flat.skipped.length === 0) {
      out.push(
        "Nothing was recorded as skipped. Every rule this build knows about had the input it needs.",
        "",
      );
    } else {
      out.push(
        "These rules had no input to run against, so they neither passed nor failed:",
        "",
      );
      for (const text of flat.skipped) out.push(`- ${oneLine(text)}`);
      out.push("");
    }

    out.push(`_${gtaReportSourceStamp(report, options)}_`);
    return out.join("\n");
  } catch (error) {
    return failureDocument("markdown", error, options);
  }
}

// ─── CSV ─────────────────────────────────────────────────────────────────────

const CSV_HEADER = [
  "scope",
  "resource",
  "resource_origin",
  "vehicle",
  "severity",
  "code",
  "title",
  "detail",
  "entity_kind",
  "entity_id",
  "tags",
  "file",
  "line",
  "column",
  "fix",
  "fix_confidence",
  // Appended rather than slotted in beside `resource_origin`, where it reads
  // better, because a column inserted mid-table renumbers every column after it
  // for anything reading this file positionally. It is the last resort of the
  // three resource columns and it is in the last position; that is consistent
  // enough to defend and it does not move anybody's existing formula.
  "resource_identity",
];

/**
 * Every finding as a spreadsheet, plus the rows that say what was not checked.
 *
 * `resource_origin` sits next to `resource` rather than at the end because the
 * two are read together: `resource` is the FiveM name and is allowed to repeat —
 * two vendor archives that both ship `police/` are the case this tool exists for
 * — so a `police` row that does not say which archive it came from is a row
 * nobody can act on. It is blank for a scan that had nothing to attribute, which
 * is most of them.
 *
 * The skipped checks and the source stamp ride in the same table under their own
 * `scope` values rather than as a banner above it, because a banner is what
 * stops a CSV from parsing. Filtering `scope` to the three finding scopes gives
 * a clean findings table; leaving them in is what stops a spreadsheet from being
 * the one copy of the report that quietly claims everything was checked.
 */
export function toCsv(
  report: GtaResourceReport | GtaResourceSetReport,
  options: GtaReportExportOptions = {},
): string {
  try {
    const flat = flatten(report);
    const lines = [CSV_HEADER.join(",")];

    for (const row of allRows(flat)) {
      const finding = row.finding;
      lines.push(
        [
          row.scope,
          row.resource,
          row.origin ?? "",
          row.vehicle,
          finding.severity,
          finding.code,
          finding.title,
          finding.detail,
          finding.entity?.kind ?? "",
          finding.entity?.id ?? "",
          (finding.tags ?? []).join(" "),
          finding.file ?? "",
          finding.line === undefined ? "" : String(finding.line),
          finding.column === undefined ? "" : String(finding.column),
          finding.fix?.label ?? "",
          finding.fix?.confidence ?? "",
          row.identity ?? "",
        ]
          .map(csvCell)
          .join(","),
      );
    }

    const skippedRow = (resource: string, origin: string, identity: string, text: string) =>
      lines.push(
        [
          "skipped",
          resource,
          origin,
          "",
          "",
          "",
          "A check did not run",
          text,
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          identity,
        ]
          .map(csvCell)
          .join(","),
      );
    for (const resource of flat.resources) {
      // Carries the identity for the same reason the finding rows do: two packs
      // that share a name and an origin also share every skipped-check sentence,
      // and a reader filtering this file on `resource` would see one pack's
      // caveats twice with nothing saying they belong to two different folders.
      for (const text of resource.skipped) {
        skippedRow(resource.name, resource.origin, resource.identity, text);
      }
    }
    for (const text of flat.crossSkipped) skippedRow("", "", "", text);

    if (flat.unreadable > 0) {
      skippedRow(
        "",
        "",
        "",
        `${flat.unreadable} entries in this report could not be read and are not in this file.`,
      );
    }

    lines.push(
      ["source", "", "", "", "", "", GTA_REPORT_TOOL, gtaReportSourceStamp(report, options), "", "", "", "", "", "", "", "", ""]
        .map(csvCell)
        .join(","),
    );

    // CRLF, like `resmonRowsToCsv`: it is what Excel expects from a file it did
    // not write, and every other reader accepts it.
    return lines.join("\r\n");
  } catch (error) {
    return `${CSV_HEADER.join(",")}\r\n${["source", "", "", "", "", "", GTA_REPORT_TOOL, failureSentence("csv", error)]
      .concat(["", "", "", "", "", "", "", "", ""])
      .map(csvCell)
      .join(",")}`;
  }
}

// ─── JSON ────────────────────────────────────────────────────────────────────

/**
 * The whole report as one document a script can hold onto.
 *
 * Both input shapes come out the same way — a single resource is a set of one —
 * so a script that walks a directory of packs does not need two code paths, and
 * a diff between yesterday's export and today's compares like with like. That is
 * the thing the competitor's tools tell users to do and give them no way to do.
 *
 * The document is assembled field by field rather than by stringifying the report
 * object, for two reasons: a report holds a `RegExp` inside `fix.edits` that
 * would serialise as `{}`, and a future field added to `GtaResourceReport` should
 * not silently change a format other people's scripts parse.
 */
export function toJson(
  report: GtaResourceReport | GtaResourceSetReport,
  options: GtaReportExportOptions = {},
): string {
  try {
    const flat = flatten(report);
    const findings = allFindings(flat);
    // Counted over every finding the document carries, so `error + warning +
    // info` can be reconciled against `findings` by whoever parses this.
    const counts = countSeverities(findings);
    const vanilla = vanillaIndexStamp(report, options.vanillaNames);

    const rowJson = (row: GtaExportRow) => ({
      scope: row.scope,
      resource: row.resource,
      resourceOrigin: row.origin ?? "",
      resourceIdentity: row.identity ?? "",
      vehicle: row.vehicle,
      code: row.finding.code,
      severity: row.finding.severity,
      title: row.finding.title,
      detail: row.finding.detail,
      entity: row.finding.entity ?? null,
      tags: row.finding.tags ?? [],
      emergency: isEmergencyFinding(row.finding),
      file: row.finding.file ?? null,
      line: row.finding.line ?? null,
      column: row.finding.column ?? null,
      fix: row.finding.fix ?? null,
    });

    const document = {
      schema: GTA_REPORT_SCHEMA,
      tool: GTA_REPORT_TOOL,
      generatedAt: stampInstant(options.generatedAt),
      reportKind: flat.kind,
      vanillaIndex: vanilla,
      staticCheckNote: GTA_REPORT_STATIC_CHECK_NOTE,
      summary: {
        verdict: headlineVerdict(counts),
        error: counts.error,
        warning: counts.warning,
        info: counts.info,
        findings: findings.length,
        resources: flat.resources.length,
        vehicles: flat.resources.reduce((sum, resource) => sum + resource.vehicles.length, 0),
        skipped: flat.skipped.length,
        unreadable: flat.unreadable,
      },
      skipped: flat.skipped,
      crossResourceFindings: flat.cross.map(rowJson),
      resources: flat.resources.map((resource) => ({
        name: resource.name,
        // A script diffing two scans keys on these: the name alone cannot tell
        // one `police` pack from the other `police` pack in the same run.
        identity: resource.identity,
        origin: resource.origin,
        kind: resource.kind,
        verdict: resource.verdict,
        manifest: resource.manifest,
        checks: resource.checks,
        streamAssets: resource.streamAssets,
        skipped: resource.skipped,
        vehicles: resource.vehicles,
        findings: resource.rows.map(rowJson),
      })),
    };

    return JSON.stringify(document, null, 2);
  } catch (error) {
    // Valid JSON either way: a script that gets this back can read the reason,
    // where a thrown exception in a copy handler leaves it with nothing at all.
    return JSON.stringify(
      {
        schema: GTA_REPORT_SCHEMA,
        tool: GTA_REPORT_TOOL,
        generatedAt: stampInstant(options.generatedAt),
        error: failureSentence("json", error),
      },
      null,
      2,
    );
  }
}

// ─── When the report itself defeats us ───────────────────────────────────────

function failureSentence(format: string, error: unknown): string {
  const reason = error instanceof Error && error.message ? error.message : String(error);
  return (
    `This report could not be written as ${format}: ${oneLine(reason)}. ` +
    "The scan on screen is unaffected — nothing was lost except this file."
  );
}

function failureDocument(
  format: string,
  error: unknown,
  options: GtaReportExportOptions,
): string {
  return [
    "# Report export failed",
    "",
    failureSentence(format, error),
    "",
    // `null` rather than the report: the report is what just defeated the
    // exporter, so nothing it says about its own checks can be relied on, and
    // the stamp says that instead of picking one of the two confident answers.
    `_${gtaReportSourceStamp(null, options)}_`,
  ].join("\n");
}
